import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../../layouts/PageContainer';

import { Skeleton, SkeletonCircle, SkeletonBar } from '../../util/skeleton/Skeleton';
import { useMySale } from '../../DataStore/MySaleContext';
import { useAuth } from '../../DataStore/AuthContext';
import { formatMoney, tell_when } from '../../util/araknerdlib';
import PageHeader from '../../components/Nav/PageHeader';
import VoucherPagination from '../../components/Vendor/VoucherPagination';

/* ---------- Skeleton ---------- */
const VouchersListSkeleton = ({ rows = 8 }) => (
  <div className="of-card">
    <div className="of-list-head">
      <div>
        <Skeleton w="170px" h={18} />
        <Skeleton w="120px" h={12} style={{ marginTop: 6 }} />
      </div>
      <Skeleton w="110px" h={34} />
    </div>

    <div className="of-list">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="of-item">
          <div className="of-item-left">
            <SkeletonCircle size={40} />
            <div style={{ marginLeft: 12, flex: 1 }}>
              <Skeleton w="160px" h={14} />
              <Skeleton w="220px" h={12} style={{ marginTop: 6 }} />
            </div>
          </div>
          <div className="of-item-right-stack">
            <Skeleton w="70px" h={14} />
            <Skeleton w="120px" h={12} style={{ marginTop: 6 }} />
          </div>
        </div>
      ))}
    </div>

    <div className="p-3 pt-2">
      <SkeletonBar h={38} />
    </div>
  </div>
);

/* ---------- Page ---------- */
const VendorVouchers = () => {
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);

  const {
    getVoucherTransactions,
    transactions,
    metaData,
    filters,
    setFilters,
    setCurrentPage,
    loading,
    error,
    setMounted
  } = useMySale();

  const { connectedProfiles } = useAuth();

  const money = useMemo(
    () => (amount) => formatMoney(amount, connectedProfiles?.currency),
    [connectedProfiles]
  );

  /* initial mount */
  useEffect(() => {
    setMounted(true);
    getVoucherTransactions();
  }, []);

  const refresh = () => {
    setCurrentPage(1);
    getVoucherTransactions(true);
  };

  const applyFilter = () => {
    setCurrentPage(1);
    getVoucherTransactions(true);
  };

  /* pagination */
  const page = Number(metaData?.page || 1);
  const totalPages = Number(metaData?.totalPages || 1);

  const canPrev = page > 1 && !loading;
  const canNext = page < totalPages && !loading;

  const goPrev = () => {
    if (!canPrev) return;
    setCurrentPage(page - 1);
    getVoucherTransactions(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goNext = () => {
    if (!canNext) return;
    setCurrentPage(page + 1);
    getVoucherTransactions(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageContainer>
       <PageHeader
        title="Vouchers"
        breadcrumb={<li className="breadcrumb-item active" aria-current="page">Vouchers</li>}
         primaryButtons={[
          {
            label: 'Refresh',
            icon: 'fas fa-sync',
            className: 'btn-outline-secondary',
            onClick: () => {
              refresh();
            },
          },
        ]}
      />
      <div className="of-page">

        {/* ---------- Header ---------- */}
        <div className="of-page-head">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-sm of-btn-ghost mr-2"
              onClick={() => navigate('/dashboard')}
            >
              <i className="fas fa-arrow-left mr-2" />
              Dashboard
            </button>

            <button
              className="btn btn-sm of-btn-ghost mr-2"
              onClick={() => setShowFilters((v) => !v)}
              type="button"
            >
              <i className={`fas fa-filter mr-2 ${showFilters ? 'text-info' : ''}`} />
              Filter
            </button>

          </div>
        </div>

        {/* ---------- Filters (collapsible) ---------- */}
        <div className={`of-filter-wrap ${showFilters ? 'open' : ''}`}>
          <div className="of-card mb-3 p-3">
            <div className="row">
              <div className="col-md-4">
                <label>From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.from || ''}
                  onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                />
              </div>

              <div className="col-md-4">
                <label>To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.to || ''}
                  onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                />
              </div>

              <div className="col-md-4 d-flex align-items-end mt-2">
                <button className="btn of-btn-primary btn-block" onClick={applyFilter} type="button">
                  Apply filter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Body ---------- */}
        {loading && (!transactions || transactions.length === 0) ? (
          <VouchersListSkeleton rows={10} />
        ) : (
          <div className="of-card">
            <div className="of-list-head">
              <div>
                <div className="of-list-title">All Vouchers</div>
                <div className="of-list-sub">
                  {metaData?.total || 0} total vouchers
                </div>
              </div>

              <div className="of-muted small">
                Page {page} of {totalPages}
              </div>
            </div>

            {error ? (
              <div className="p-3 alert alert-danger">{error}</div>
            ) : !Array.isArray(transactions) || transactions.length === 0 ? (
              <div className="p-4 text-center text-muted">
                No vouchers found.
              </div>
            ) : (
              <div className="of-list overflow-auto" style={{maxHeight: '520px'}}>
                {transactions.map((v) => (
                  <div key={v.id} className="of-item">
                    <div className="of-item-left">
                      <div className="of-item-ico">
                        <i className="fas fa-wifi" />
                      </div>

                      <div>
                        <div className="of-item-title">{v.plan_name.toLowerCase()}</div>
                        <div className="of-item-sub">
                          <span className="of-code">{v.code}</span>
                          <span className="of-sep">•</span>
                          <span className={`badge badge-${v.status === 'used' ? 'success' : 'secondary'}`}>
                            {String(v.status).toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="of-item-right-stack">
                      <div className="of-item-amount">{money(v.amount)}</div>
                      <div className="of-item-date">{tell_when(v.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ---------- Pagination (Prev / Next) ---------- */}
            {totalPages > 1 && (
             <VoucherPagination metaData={metaData} onPageChange={(page) => {setCurrentPage(page);}} />
            )}
          </div>
        )}
      </div>

     
    </PageContainer>
  );
};

export default VendorVouchers;
